import { Modal } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ConfirmModalData } from './ConfirmModalContent';
import { ConfirmModalContent } from './ConfirmModalContent';

export type { ConfirmModalData } from './ConfirmModalContent';
export { useConfirm } from './useConfirm';

export type ConfirmModalFunctions = {
	/**
	 * ### Accept Action
	 * #### This will close the modal in addition to executing this callback
	 * The `onAccept` prop handles the callback when the accept button is pressed
	 * in an open modal.
	 */
	onAccept?: () => void;
	/**
	 * ### Closing the Modal
	 * The `onClose` prop handles the callback when the close icon is pressed
	 * in an open modal. If the `onClose` prop is not passed, the close icon
	 * will not render. The modal can still be closed by clicking outside the
	 * modal or pressing the `ESC` key.
	 */
	onClose?: () => void;
	/**
	 * ### Reject Action
	 * #### This will close the modal in addition to executing this callback
	 * The `onReject` prop handles the callback when the reject button is pressed
	 * in an open modal.
	 */
	onReject?: () => void;
	/** The 'trigger' which will handling the opening and closing of the modal.
	 * React Aria will handle the state of the modal internally, and expects a
	 * button element here. It applies necessary data attributes to the button
	 * to manage the modal state.
	 */
	trigger: React.ReactNode;
};

export type ConfirmModalOptions = ConfirmModalData & ConfirmModalFunctions;

/**
 * Can be used to render a button that opens a confirm modal.
 * This is useful on situations where the modal flow does not need to be based on a hook.
 *
 * For an imperative (`await confirm()`) flow, use the `useConfirm` hook from
 * `#src/app/shared/components/modals` instead.
 *
 * Options can be passed as props to the component.
 *
 * @example
 * <ConfirmModal
 * 	buttonsOrientation="horizontal"
 * 	cancelLabel="Cancel"
 * 	confirmLabel="Confirm"
 * 	leadingSlot={<SampleIcon />}
 * 	onAccept={() => {}}
 * 	onClose={() => {}}
 * 	onReject={() => {}}
 * 	subTitle="Subtitle"
 * 	text={['Text1', 'Text2']}
 * 	title="Title"
 * 	trigger={
 * 		<Button data-qa="open-modal-button" data-testid="open-modal-button">
 * 			Open Modal
 * 		</Button>
 * 	}
 * />
 */
export const ConfirmModal: React.FC<ConfirmModalOptions> = ({
	onAccept,
	onClose,
	onReject,
	trigger,
	...contentProps
}) => {
	const { translate } = useI18n();

	const modalContent = (
		<ConfirmModalContent {...contentProps} onAccept={() => onAccept?.()} onReject={() => onReject?.()} />
	);

	if (onClose) {
		return (
			<Modal
				onClose={onClose}
				closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
				trigger={trigger}
			>
				{modalContent}
			</Modal>
		);
	}

	return <Modal trigger={trigger}>{modalContent}</Modal>;
};
